/**
 * Coder API Client
 * Interfaces with orchestration-service coder endpoints
 */

// API base URL
const API_BASE = '/api/orchestration';

/**
 * Sprite configuration
 */
export interface SpriteConfig {
  machineSize: string;
  region: string;
  timeout: number;
  memoryMb: number;
  cpus: number;
  image: string;
}

/**
 * Execution template
 */
export interface ExecutionTemplate {
  id: string;
  name: string;
  description: string;
  spriteConfig: SpriteConfig;
  setupCommands: string[];
  executeCommand: string;
  cleanupCommands: string[];
  environmentVariables: Record<string, string>;
}

/**
 * Execution story input
 */
export interface ExecutionStory {
  id: string;
  title: string;
  description: string;
  acceptanceCriteria: string[];
}

/**
 * Execution epic input
 */
export interface ExecutionEpic {
  id: string;
  title: string;
  description: string;
  stories: ExecutionStory[];
}

/**
 * Code execution request
 */
export interface ExecuteCodeRequest {
  worktreeId: string;
  boardId: string;
  epics: ExecutionEpic[];
  config?: {
    maxConcurrentEpics?: number;
    maxConcurrentStories?: number;
    templateId?: string;
    verifyChecks?: Array<'lint' | 'typecheck' | 'test' | 'build'>;
    repositoryUrl?: string;
    branch?: string;
  };
}

/**
 * Execution job
 */
export interface ExecutionJob {
  batchId: string;
  status: string;
  startTime: string;
  closeTime?: string;
  progress?: {
    step: string;
    progress: number;
    message: string;
  };
  executionState?: {
    epicsCompleted: number;
    epicsTotal: number;
    storiesCompleted: number;
    storiesTotal: number;
    storiesSuccessful: number;
    storiesFailed: number;
    activeSprites: number;
    errors: string[];
  };
  result?: {
    batchId: string;
    success: boolean;
    summary: {
      epicsProcessed: number;
      storiesProcessed: number;
      storiesSuccessful: number;
      storiesFailed: number;
      spritesUsed: number;
    };
    duration: number;
  };
}

/**
 * Active sprite
 */
export interface ActiveSprite {
  id: string;
  workflowId: string;
  epicId: string;
  status: string;
}

/**
 * Fly.io machine (from Machines API)
 */
export interface FlyMachine {
  machineId: string;
  name: string;
  state: string;
  region: string;
  instanceId: string;
  privateIp: string;
  createdAt: string;
  updatedAt: string;
  hostStatus: string;
  spriteId: string;
  spriteName: string;
  workflowId: string;
  exitCode: number | null;
  oomKilled: boolean;
  exitedAt: string | null;
  config: {
    cpuKind: string;
    cpus: number;
    memoryMb: number;
    image: string;
  };
  imageRef: {
    registry: string;
    repository: string;
    tag: string;
    digest: string;
  } | null;
  env: Record<string, string>;
  events: Array<{
    type: string;
    status: string;
    timestamp: string;
    exitCode: number | null;
    oomKilled: boolean | null;
    exitedAt: string | null;
  }>;
  lifespanSeconds: number;
}

/**
 * Coder API client
 */
export const coderApi = {
  /**
   * Start code execution
   */
  async execute(request: ExecuteCodeRequest): Promise<{ batchId: string; status: string }> {
    const response = await fetch(`${API_BASE}/coder/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(`Failed to start execution: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * List execution jobs
   */
  async listExecutions(options?: {
    worktreeId?: string;
    status?: string;
    limit?: number;
  }): Promise<{ executions: ExecutionJob[]; count: number }> {
    const params = new URLSearchParams();
    if (options?.worktreeId) params.set('worktreeId', options.worktreeId);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', String(options.limit));

    const response = await fetch(`${API_BASE}/coder/executions?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to list executions: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get execution status
   */
  async getExecution(batchId: string): Promise<ExecutionJob> {
    const response = await fetch(`${API_BASE}/coder/executions/${batchId}`);
    if (!response.ok) {
      throw new Error(`Failed to get execution: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Cancel execution
   */
  async cancelExecution(batchId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/coder/executions/${batchId}/cancel`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to cancel execution: ${response.statusText}`);
    }
  },

  /**
   * List templates
   */
  async listTemplates(): Promise<{ templates: ExecutionTemplate[]; count: number }> {
    const response = await fetch(`${API_BASE}/coder/templates`);
    if (!response.ok) {
      throw new Error(`Failed to list templates: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get template
   */
  async getTemplate(templateId: string): Promise<ExecutionTemplate> {
    const response = await fetch(`${API_BASE}/coder/templates/${templateId}`);
    if (!response.ok) {
      throw new Error(`Failed to get template: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * List active sprites
   */
  async listSprites(): Promise<{ sprites: ActiveSprite[]; count: number }> {
    const response = await fetch(`${API_BASE}/coder/sprites`);
    if (!response.ok) {
      throw new Error(`Failed to list sprites: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * List Fly.io machines
   */
  async listMachines(): Promise<{ machines: FlyMachine[]; count: number; app: string; dashboardUrl: string }> {
    const response = await fetch(`${API_BASE}/coder/machines`);
    if (!response.ok) {
      if (response.status === 503) {
        return { machines: [], count: 0, app: '', dashboardUrl: '' };
      }
      throw new Error(`Failed to list machines: ${response.statusText}`);
    }
    return response.json();
  },
};

export default coderApi;
